import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import classNames from 'classnames';
import { oneOf, string } from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { updateCurrentUserLocale } from '../../ducks/user.duck';
import {
  SUPPORTED_LOCALES,
  alternateLocale,
  normalizeLocale,
  replaceLocaleInPathname,
  setStoredLocale,
} from '../../util/locale';
import { useIntl } from '../../util/reactIntl';

import css from './LanguageSwitcher.module.css';

const LOCALE_LABELS = {
  en: 'English',
  ar: 'العربية',
};

/**
 * Language switcher: navigates to the same path under the other locale,
 * persists preference, and syncs privateData.locale when authenticated.
 */
export const LanguageSwitcher = props => {
  const { className, rootClassName, variant } = props;
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const [anchorEl, setAnchorEl] = useState(null);

  const currentLocale = normalizeLocale(intl.locale) || 'en';

  const switchTo = nextLocale => {
    if (nextLocale === currentLocale || !SUPPORTED_LOCALES.includes(nextLocale)) {
      return;
    }
    setStoredLocale(nextLocale);
    if (isAuthenticated) {
      dispatch(updateCurrentUserLocale(nextLocale)).catch(() => {
        // Preference is already stored locally; profile sync can retry later.
      });
    }
    const nextPath = replaceLocaleInPathname(location.pathname, nextLocale);
    history.push(`${nextPath}${location.search}${location.hash}`);
  };

  const classes = classNames(rootClassName || css.root, className, {
    [css.segmented]: variant === 'segmented',
  });
  const currentLanguageLabel = intl.formatMessage(
    { id: 'LanguageSwitcher.current' },
    { language: LOCALE_LABELS[currentLocale] }
  );

  if (variant === 'dropdown') {
    return (
      <div className={classNames(css.dropdown, className)}>
        <button
          type="button"
          className={css.trigger}
          aria-haspopup="listbox"
          aria-expanded={Boolean(anchorEl)}
          aria-label={currentLanguageLabel}
          onClick={event => setAnchorEl(event.currentTarget)}
        >
          <span>{currentLocale.toUpperCase()}</span>
          <KeyboardArrowDownIcon className={css.chevron} aria-hidden="true" />
        </button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              className: css.menuPaper,
            },
            list: {
              className: css.menuList,
            },
          }}
        >
          {SUPPORTED_LOCALES.map(locale => (
            <MenuItem
              key={locale}
              className={classNames(css.menuItem, {
                [css.menuItemSelected]: locale === currentLocale,
              })}
              selected={locale === currentLocale}
              lang={locale}
              onClick={() => {
                switchTo(locale);
                setAnchorEl(null);
              }}
            >
              <span className={css.menuItemBorder} aria-hidden="true" />
              {LOCALE_LABELS[locale]}
            </MenuItem>
          ))}
        </Menu>
      </div>
    );
  }

  return (
    <div
      className={classes}
      role="group"
      aria-label={intl.formatMessage({ id: 'LanguageSwitcher.label' })}
    >
      {SUPPORTED_LOCALES.map(locale => {
        const isActive = locale === currentLocale;
        return (
          <button
            key={locale}
            type="button"
            className={classNames(css.option, { [css.active]: isActive })}
            aria-pressed={isActive}
            onClick={() => switchTo(locale)}
            lang={locale}
          >
            {LOCALE_LABELS[locale]}
          </button>
        );
      })}
      <span className={css.srOnly}>
        {intl.formatMessage(
          { id: 'LanguageSwitcher.current' },
          { language: LOCALE_LABELS[currentLocale] }
        )}
      </span>
    </div>
  );
};

LanguageSwitcher.defaultProps = {
  className: null,
  rootClassName: null,
  variant: 'segmented',
};

LanguageSwitcher.propTypes = {
  className: string,
  rootClassName: string,
  variant: oneOf(['segmented', 'dropdown']),
};

export default LanguageSwitcher;

// Re-export helper for callers that only need the alternate locale path
export const getAlternateLocalePath = (pathname, locale) =>
  replaceLocaleInPathname(pathname, alternateLocale(locale));
